"use client";

import React, { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars, faTimes } from "@fortawesome/free-solid-svg-icons";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";

interface NavItem {
  translationKey: string;
  href: string;
}

interface NavBarGlobProps {
  navItems?: NavItem[];
  logoPath?: string;
}

const NavBarGlob: React.FC<NavBarGlobProps> = ({
  navItems = [],
  logoPath = "/loghi/BeehiveLogo.png",
}) => {
  const { t } = useTranslation("common");
  const [isOpen, setIsOpen] = useState(false);
  const [showStickyNav, setShowStickyNav] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setShowStickyNav(true);
      } else {
        setShowStickyNav(false);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleLinkClick = () => setIsOpen(false);

  return (
    <>
      {/* Navbar iniziale */}
      <div
        className={`w-full z-40 transition-all duration-300 ${
          showStickyNav ? "opacity-0 pointer-events-none" : "opacity-100"
        }`}
      >
        <div className="sticky top-0 bg-white/95 border-b border-gray-100 xl:rounded-t-[80px]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16 lg:h-20">
              {/* Logo */}
              <Link href="/" className="flex items-center flex-shrink-0">
                <Image
                  width={500}
                  height={300}
                  src={logoPath}
                  alt="Logo"
                  className="h-10 sm:h-12 lg:h-16 w-auto"
                />
              </Link>

              {/* Navigation Desktop */}
              <nav className="hidden md:flex items-center space-x-6 lg:space-x-8">
                {navItems.map((item) => (
                  <Link
                    key={item.translationKey}
                    href={item.href}
                    className="relative text-gray-700 hover:text-orange-600 font-medium transition-colors duration-200 py-2 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:w-0 hover:after:w-full after:bg-orange-600 after:transition-all after:duration-300"
                  >
                    {t(`navbar.${item.translationKey}`)}
                  </Link>
                ))}
              </nav>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden relative z-50 p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
                aria-label={t("navbar.toggleMenu")}
                aria-expanded={isOpen}
              >
                <FontAwesomeIcon
                  icon={isOpen ? faTimes : faBars}
                  className="w-5 h-5"
                />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Navbar sticky dopo scroll */}
      <motion.div
        initial={{ y: -100, opacity: 0 }}
        animate={{
          y: showStickyNav ? 0 : -100,
          opacity: showStickyNav ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        className={`fixed top-0 w-full z-50 ${
          showStickyNav ? "pointer-events-auto" : "pointer-events-none"
        }`}
      >
        <div className="bg-white/98 backdrop-blur-md shadow-lg border-b border-gray-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-14 lg:h-16">
              {/* Logo */}
              <Link href="/" className="flex items-center flex-shrink-0">
                <Image
                  width={200}
                  height={100}
                  src={logoPath}
                  alt="Logo"
                  className="h-8 sm:h-10 lg:h-12 w-auto"
                />
              </Link>

              {/* Navigation Desktop compatta */}
              <nav className="hidden md:flex items-center space-x-4 lg:space-x-6">
                {navItems.map((item) => (
                  <Link
                    key={item.translationKey}
                    href={item.href}
                    className="relative text-gray-700 hover:text-orange-600 font-medium text-sm lg:text-base transition-colors duration-200 py-2 after:absolute after:bottom-0 after:left-1/2 after:-translate-x-1/2 after:h-[2px] after:w-0 hover:after:w-full after:bg-orange-600 after:transition-all after:duration-300"
                  >
                    {t(`navbar.${item.translationKey}`)}
                  </Link>
                ))}
              </nav>

              {/* Mobile menu button */}
              <button
                onClick={() => setIsOpen(!isOpen)}
                className="md:hidden p-2 rounded-lg text-gray-700 hover:bg-gray-100 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-orange-500"
                aria-label={t("navbar.toggleMenu")}
                aria-expanded={isOpen}
              >
                <FontAwesomeIcon
                  icon={isOpen ? faTimes : faBars}
                  className="w-4 h-4"
                />
              </button>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Mobile Navigation */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed top-0 left-0 right-0 z-[60] md:hidden bg-white border-b border-gray-200 shadow-lg"
            style={{
              marginTop: showStickyNav ? "56px" : "64px",
            }}
          >
            <div className="px-4 py-4 space-y-2 max-w-7xl mx-auto">
              {navItems.map((item) => (
                <Link
                  key={item.translationKey}
                  href={item.href}
                  className="block px-4 py-3 text-gray-700 hover:text-orange-600 hover:bg-orange-50 rounded-lg font-medium transition-all duration-200"
                  onClick={handleLinkClick}
                >
                  {t(`navbar.${item.translationKey}`)}
                </Link>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default NavBarGlob;
